import type {
  AccountList,
  ClientCreate,
  CurrencyCode,
  DashboardSummary,
  Document,
  DocumentCreateRequest,
  DocumentDownloadUrlResponse,
  DocumentList,
  DocumentUploadUrlRequest,
  DocumentUploadUrlResponse,
  EngagementLetterAcceptRequest,
  EngagementLetterTemplate,
  IncomeStatement,
  JournalEntryCreate,
  LedgerEntryList,
  MeResponse,
  OnboardingResponse,
  Problem,
  SessionResponse,
  TrialBalance,
} from '@keobook/shared';
import { env } from '../env';

// --- Invoicing types (PR-1; will move to @keobook/shared later) ---

export type Address = {
  line1: string | null;
  line2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string | null;
};

export type Customer = {
  id: string;
  displayName: string;
  companyName: string | null;
  primaryEmail: string | null;
  billAddr: Address;
  shipAddr: Address;
  defaultTermsDays: number;
  defaultTaxCodeId: string | null;
  notes: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
};

export type InvoiceItem = {
  id: string;
  name: string;
  type: 'service' | 'noninventory';
  unitPriceCents: number;
  description: string | null;
  incomeAccountId: string;
  taxable: boolean;
  active: boolean;
  createdAt: string;
  updatedAt: string;
};

export type InvoiceLine = {
  id: string;
  lineNo: number;
  itemId: string | null;
  description: string;
  qty: number;
  unitPriceCents: number;
  amountCents: number;
  taxCodeId: string | null;
  incomeAccountIdSnapshot: string;
};

export type Invoice = {
  id: string;
  customerId: string;
  docNumber: string;
  txnDate: string;
  dueDate: string;
  termsDays: number;
  billAddr: Address;
  shipAddr: Address;
  privateNote: string | null;
  customerMemo: string | null;
  subtotalCents: number;
  discountCents: number;
  taxCents: number;
  totalCents: number;
  balanceCents: number;
  /** Derived: lifecycle === 'posted' && balanceCents === 0 && total > 0. */
  paid: boolean;
  /** Sum of refunded amounts across payments applied to this invoice. */
  refundedAmountCents: number;
  /** Sum of late fees applied to this invoice. Adds to balanceCents. */
  lateFeeAmountCents: number;
  /** Any payment on this invoice has an open Stripe dispute (status != 'won'). */
  disputed: boolean;
  lifecycle: 'draft' | 'posted' | 'void';
  sentAt: string | null;
  viewedAt: string | null;
  voidedAt: string | null;
  voidReason: string | null;
  createdAt: string;
  updatedAt: string;
  lines: InvoiceLine[];
};

/**
 * Typed thin fetch wrappers around the API. Each method matches a route in
 * apps/api/src/routes/ and is named after the action, not the path.
 *
 * Auth tokens come from a provider the AuthProvider registers (see
 * setAuthTokenProvider). In IdP mode the provider returns a fresh Firebase
 * ID token (auto-refreshed); in dev mode it returns the stored dev JWT.
 * Call sites don't pass tokens.
 */

const BASE = env.VITE_API_BASE_URL;

let getAuthToken: () => Promise<string | null> = async () => null;
export function setAuthTokenProvider(fn: () => Promise<string | null>): void {
  getAuthToken = fn;
}

// The client the user is currently acting on (for multi-client accountants).
// Attached as X-Client-Id on every request so the server scopes correctly.
let getClientId: () => string | null = () => null;
export function setClientIdProvider(fn: () => string | null): void {
  getClientId = fn;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public problem: Problem | { title: string; status: number; [k: string]: unknown } | string,
  ) {
    // Prefer detail (Zod / specific validation message) over title (generic
    // category like "Invalid request body"). Falls through to HTTP <status>
    // when nothing structured comes back.
    let message: string;
    if (typeof problem === 'string') {
      message = problem;
    } else if (
      typeof problem === 'object' &&
      'detail' in problem &&
      typeof (problem as { detail?: unknown }).detail === 'string' &&
      (problem as { detail: string }).detail.trim().length > 0
    ) {
      const title = 'title' in problem ? (problem as { title?: string }).title : undefined;
      const detail = (problem as { detail: string }).detail;
      message = title && title !== detail ? `${title}: ${detail}` : detail;
    } else if (typeof problem === 'object' && 'title' in problem) {
      message = (problem as { title: string }).title;
    } else {
      message = `HTTP ${status}`;
    }
    super(message);
    this.name = 'ApiError';
  }
}

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  clientId?: string;
  /** Skip attaching the auth token (for the public dev session endpoint). */
  anonymous?: boolean;
  signal?: AbortSignal;
};

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (!options.anonymous) {
    const token = await getAuthToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }
  const activeClientId = options.clientId ?? getClientId();
  if (activeClientId) headers['X-Client-Id'] = activeClientId;

  const res = await fetch(`${BASE}${path}`, {
    method: options.method ?? 'GET',
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    signal: options.signal,
  });

  const contentType = res.headers.get('content-type') ?? '';
  const isJson = contentType.includes('json');
  const body = isJson ? await res.json() : await res.text();

  if (!res.ok) {
    throw new ApiError(res.status, body);
  }
  return body as T;
}

// --- Referral program types ---
export type ReferralPartnerStatus = 'pending' | 'active' | 'suspended' | 'closed';
export type ReferralPartnerSummary = {
  code: string;
  displayName?: string;
  status: ReferralPartnerStatus;
  agreementAccepted: boolean;
  shareLink: string;
};
export type ReferralAgreementTemplate = {
  version: string;
  title: string;
  effectiveDate: string;
  body: string;
};
export type ReferralPayout = {
  id: string;
  totalCents: number;
  status: 'pending' | 'paid' | 'failed';
  commissionCount: number;
  createdAt: string;
  paidAt: string | null;
};
export type ReferralMe = {
  partner: {
    code: string;
    displayName: string;
    status: ReferralPartnerStatus;
    agreementAccepted: boolean;
    shareLink: string;
    payoutsEnabled: boolean;
  };
  summary: {
    totalReferrals: number;
    converted: number;
    pending: number;
    churned: number;
    earningsCents: { accrued: number; approved: number; paid: number };
  };
  referrals: Array<{
    id: string;
    status: 'pending' | 'converted' | 'churned';
    attributedAt: string;
    convertedAt: string | null;
  }>;
  payouts: ReferralPayout[];
};

export type AdminReferralCommission = {
  id: string;
  commissionCents: number;
  grossCents: number;
  rateBps: number;
  status: string;
  createdAt: string;
  partnerCode: string;
  partnerEmail: string;
};

// --- Collaborator invites types ---
export type CollaboratorRole = 'owner' | 'member' | 'accountant' | 'bookkeeper';
export type Collaborator = {
  userId: string;
  email: string;
  fullName: string | null;
  role: CollaboratorRole;
  permissions: string[];
};
export type PendingInvitation = {
  id: string;
  email: string;
  role: CollaboratorRole;
  createdAt: string;
  expiresAt: string;
};
export type CollaboratorsResponse = {
  members: Collaborator[];
  pendingInvitations: PendingInvitation[];
  seats: { used: number; included: number };
};

// --- Bank connections (Stripe Financial Connections) ---
export type BankConnection = {
  id: string;
  institutionName: string | null;
  last4: string | null;
  displayName: string | null;
  status: string;
  lastRefreshAt: string | null;
  transactionCount: number;
  createdAt: string;
};

export const api = {
  /** Dev-only: email/password → dev JWT. Not used in IdP (Firebase) mode. */
  devSignIn(email: string, password: string, fullName?: string) {
    return request<SessionResponse>('/v1/auth/session', {
      method: 'POST',
      anonymous: true,
      body: fullName ? { email, password, fullName } : { email, password },
    });
  },

  getMe() {
    return request<MeResponse>('/v1/me');
  },
  updateProfile(fullName: string) {
    return request<{ fullName: string | null }>('/v1/me/profile', {
      method: 'PATCH',
      body: { fullName },
    });
  },
  /** Rename the current business (client.display_name). Owner-only, server-enforced. */
  updateBusinessName(displayName: string) {
    return request<{ displayName: string }>('/v1/me/client', {
      method: 'PATCH',
      body: { displayName },
    });
  },

  onboard(payload: ClientCreate) {
    return request<OnboardingResponse>('/v1/me/onboarding', { method: 'POST', body: payload });
  },

  /**
   * Tell the API that Firebase confirmed a TOTP enrollment for this user.
   * Stamps app_user.mfa_enrolled_at so /v1/me + the admin gate see it.
   * Called from Settings.tsx after finishTotpEnrollment resolves.
   */
  notifyMfaEnrolled() {
    return request<{ mfaEnrolledAt: string }>('/v1/me/mfa/enrolled', {
      method: 'POST',
      body: {},
    });
  },

  /** Symmetric: clear the column when Firebase confirms unenrollment. */
  notifyMfaCleared() {
    return request<{ mfaEnrolledAt: null }>('/v1/me/mfa/cleared', {
      method: 'POST',
      body: {},
    });
  },

  /**
   * Send a verification email to the currently-signed-in user. Replaces
   * Firebase's sendEmailVerification(). Body is empty by design — the
   * email is read from the JWT so the endpoint can't be used to spam
   * arbitrary addresses.
   */
  sendVerificationEmail() {
    return request<{ sent: true }>('/v1/auth/email/send-verification', {
      method: 'POST',
      body: {},
    });
  },

  /**
   * Send a password-reset email. Anonymous endpoint, always returns 202
   * (no account-enumeration leak). Replaces Firebase's
   * sendPasswordResetEmail().
   */
  sendPasswordResetEmail(email: string) {
    return request<{ sent: true }>('/v1/auth/email/send-password-reset', {
      method: 'POST',
      body: { email },
    });
  },

  // --- Referral program ---

  /** Partner dashboard. Throws ApiError(404) if the user isn't a partner. */
  getReferralMe() {
    return request<ReferralMe>('/v1/referral/me');
  },
  enrollReferral(displayName: string) {
    return request<ReferralPartnerSummary>('/v1/referral/enroll', {
      method: 'POST',
      body: { displayName },
    });
  },
  getReferralAgreementTemplate() {
    return request<ReferralAgreementTemplate>('/v1/referral/agreement/template');
  },
  acceptReferralAgreement(templateVersion: string, signatureFullName: string) {
    return request<ReferralPartnerSummary>('/v1/referral/agreement/accept', {
      method: 'POST',
      body: { templateVersion, signatureFullName, accepted: true },
    });
  },
  /** Start Stripe Connect payout onboarding; returns a URL to redirect to. */
  startReferralPayoutOnboarding() {
    return request<{ url: string }>('/v1/referral/connect/onboard', { method: 'POST', body: {} });
  },
  /** Reconcile Connect payout status (called after returning from onboarding). */
  refreshReferralConnect() {
    return request<{ connected: boolean; payoutsEnabled: boolean }>(
      '/v1/referral/connect/refresh',
      { method: 'POST', body: {} },
    );
  },

  // --- Collaborators (invite accountant / bookkeeper) ---
  listCollaborators() {
    return request<CollaboratorsResponse>('/v1/clients/collaborators');
  },
  inviteCollaborator(email: string, role: 'accountant' | 'bookkeeper') {
    return request<{ invitationId: string; email: string; role: string }>(
      '/v1/clients/collaborators/invite',
      { method: 'POST', body: { email, role } },
    );
  },
  revokeCollaborator(userId: string) {
    return request<void>(`/v1/clients/collaborators/${userId}`, { method: 'DELETE' });
  },
  revokeInvitation(id: string) {
    return request<void>(`/v1/clients/invitations/${id}`, { method: 'DELETE' });
  },
  updateCollaboratorPermissions(userId: string, permissions: string[]) {
    return request<{ permissions: string[] }>(`/v1/clients/collaborators/${userId}`, {
      method: 'PATCH',
      body: { permissions },
    });
  },
  /** Public: send a confirmation code to the invited email. */
  requestInvitationOtp(token: string) {
    return request<void>('/v1/public/invitations/request-otp', {
      method: 'POST',
      anonymous: true,
      body: { token },
    });
  },
  /** Public: invite context (who/which business/what role) for the accept page. */
  getInvitation(token: string) {
    return request<{ email: string; role: string; businessName: string }>(
      `/v1/public/invitations/${encodeURIComponent(token)}`,
      { anonymous: true },
    );
  },
  acceptInvitation(token: string, otp?: string) {
    return request<{ clientId: string; businessName: string }>('/v1/invitations/accept', {
      method: 'POST',
      body: otp ? { token, otp } : { token },
    });
  },

  // --- Invoicing (PR-1: CRUD only; PR-2 adds send + PDF) ---

  listCustomers() {
    return request<{ customers: Customer[] }>('/v1/customers');
  },
  getCustomer(id: string) {
    return request<{ customer: Customer }>(`/v1/customers/${id}`);
  },
  createCustomer(body: Partial<Customer> & { displayName: string }) {
    return request<{ customer: Customer }>('/v1/customers', { method: 'POST', body });
  },
  updateCustomer(id: string, body: Partial<Customer>) {
    return request<{ customer: Customer }>(`/v1/customers/${id}`, { method: 'PATCH', body });
  },

  listItems() {
    return request<{ items: InvoiceItem[] }>('/v1/items');
  },
  createItem(body: {
    name: string;
    type?: 'service' | 'noninventory';
    unitPriceCents?: number;
    description?: string;
    incomeAccountId: string;
    taxable?: boolean;
  }) {
    return request<{ item: InvoiceItem }>('/v1/items', { method: 'POST', body });
  },
  updateItem(id: string, body: Partial<InvoiceItem>) {
    return request<{ item: InvoiceItem }>(`/v1/items/${id}`, { method: 'PATCH', body });
  },

  listInvoices(params: { customerId?: string; lifecycle?: 'draft' | 'posted' | 'void' } = {}) {
    const qs = new URLSearchParams();
    if (params.customerId) qs.set('customerId', params.customerId);
    if (params.lifecycle) qs.set('lifecycle', params.lifecycle);
    const suffix = qs.toString() ? `?${qs}` : '';
    return request<{ invoices: Invoice[] }>(`/v1/invoices${suffix}`);
  },
  getInvoice(id: string) {
    return request<{ invoice: Invoice }>(`/v1/invoices/${id}`);
  },
  createInvoice(body: {
    customerId: string;
    txnDate: string;
    dueDate?: string;
    termsDays?: number;
    privateNote?: string;
    customerMemo?: string;
    lines: Array<{
      itemId?: string;
      description: string;
      qty: number;
      unitPriceCents: number;
      taxCodeId?: string;
      incomeAccountId?: string;
    }>;
  }) {
    return request<{ invoice: Invoice }>('/v1/invoices', { method: 'POST', body });
  },
  deleteInvoice(id: string) {
    return request<void>(`/v1/invoices/${id}`, { method: 'DELETE' });
  },
  sendInvoice(id: string) {
    return request<{ invoice: Invoice; payUrl: string; emailSent: boolean }>(
      `/v1/invoices/${id}/send`,
      { method: 'POST', body: {} },
    );
  },

  // --- Public hosted invoice (no auth — token is the auth) ---

  getPublicInvoice(token: string) {
    return request<{
      tenantName: string;
      currency: CurrencyCode;
      invoice: {
        docNumber: string;
        txnDate: string;
        dueDate: string;
        customerMemo: string | null;
        subtotalCents: number;
        taxCents: number;
        totalCents: number;
        lifecycle: 'draft' | 'posted' | 'void';
        billAddr: {
          line1: string | null;
          line2: string | null;
          city: string | null;
          state: string | null;
          zip: string | null;
          country: string | null;
        };
        lines: Array<{
          description: string;
          qty: number;
          unitPriceCents: number;
          amountCents: number;
        }>;
      };
    }>(`/v1/public/invoices/${token}`, { anonymous: true });
  },
  createPublicInvoiceCheckout(token: string) {
    return request<{ url: string }>(`/v1/public/invoices/${token}/checkout`, {
      method: 'POST',
      anonymous: true,
      body: {},
    });
  },
  markInvoicePaid(
    id: string,
    body: {
      depositToAccountId: string;
      method: 'cash' | 'check' | 'ach' | 'card' | 'wire' | 'other';
      paidDate?: string;
      amountCents?: number;
      notes?: string;
    },
  ) {
    return request<{
      paymentId: string;
      journalEntryId: string | null;
      remainingBalanceCents: number;
    }>(`/v1/invoices/${id}/mark-paid`, { method: 'POST', body });
  },
  listInvoicePayments(id: string) {
    return request<{
      payments: Array<{
        id: string;
        txnDate: string;
        totalCents: number;
        appliedCents: number;
        refundedAmountCents: number;
        refundedAt: string | null;
        refundReason: string | null;
        method: string;
        depositToAccountId: string;
        depositToAccountCode: string;
        depositToAccountName: string;
        isStripe: boolean;
      }>;
    }>(`/v1/invoices/${id}/payments`);
  },

  refundPayment(
    paymentId: string,
    body: { amountCents?: number; reason?: string; refundDate?: string } = {},
  ) {
    return request<{
      paymentId: string;
      amountCents: number;
      journalEntryId: string;
      stripeRefundId: string | null;
    }>(`/v1/payments/${paymentId}/refund`, { method: 'POST', body });
  },

  listRecurringInvoices() {
    return request<{
      templates: Array<{
        id: string;
        customerId: string;
        name: string;
        frequencyUnit: 'day' | 'week' | 'month';
        frequencyInterval: number;
        nextRunDate: string;
        endDate: string | null;
        active: boolean;
        runCount: number;
        lastRunAt: string | null;
        lastRunInvoiceId: string | null;
        lines: Array<{
          description: string;
          qty: number;
          unitPriceCents: number;
          incomeAccountId: string;
        }>;
      }>;
    }>('/v1/recurring-invoices');
  },

  createRecurringInvoice(body: {
    customerId: string;
    name: string;
    frequencyUnit: 'day' | 'week' | 'month';
    frequencyInterval: number;
    nextRunDate: string;
    endDate?: string;
    dueInDays?: number;
    customerMemo?: string;
    privateNote?: string;
    lines: Array<{
      itemId?: string;
      description: string;
      qty: number;
      unitPriceCents: number;
      incomeAccountId: string;
    }>;
  }) {
    return request<{ template: { id: string } }>('/v1/recurring-invoices', {
      method: 'POST',
      body,
    });
  },

  updateRecurringInvoice(
    id: string,
    body: Partial<{
      name: string;
      frequencyUnit: 'day' | 'week' | 'month';
      frequencyInterval: number;
      nextRunDate: string;
      endDate: string | null;
      dueInDays: number | null;
      customerMemo: string | null;
      privateNote: string | null;
      active: boolean;
    }>,
  ) {
    return request<{ template: { id: string } }>(`/v1/recurring-invoices/${id}`, {
      method: 'PATCH',
      body,
    });
  },

  runRecurringInvoice(id: string) {
    return request<{
      invoiceId: string;
      nextRunDate: string;
      deactivated: boolean;
    }>(`/v1/recurring-invoices/${id}/run`, { method: 'POST', body: {} });
  },

  getDunningConfig() {
    return request<{
      enabled: boolean;
      reminderDays: number[];
      sendHourLocal: number;
      lateFeeEnabled: boolean;
      lateFeeKind: 'fixed' | 'percent';
      lateFeeAmountCents: number;
      lateFeePercentBps: number;
      lateFeeAfterDays: number;
      lateFeeMaxCount: number;
    }>('/v1/dunning/config');
  },

  updateDunningConfig(body: {
    enabled: boolean;
    reminderDays: number[];
    sendHourLocal?: number;
    lateFeeEnabled?: boolean;
    lateFeeKind?: 'fixed' | 'percent';
    lateFeeAmountCents?: number;
    lateFeePercentBps?: number;
    lateFeeAfterDays?: number;
    lateFeeMaxCount?: number;
  }) {
    return request<{
      enabled: boolean;
      reminderDays: number[];
      sendHourLocal: number;
      lateFeeEnabled: boolean;
      lateFeeKind: 'fixed' | 'percent';
      lateFeeAmountCents: number;
      lateFeePercentBps: number;
      lateFeeAfterDays: number;
      lateFeeMaxCount: number;
    }>('/v1/dunning/config', { method: 'PUT', body });
  },

  applyLateFee(id: string, body: { amountCents?: number; reason?: string } = {}) {
    return request<{
      feeId: string;
      amountCents: number;
      journalEntryId: string;
    }>(`/v1/invoices/${id}/apply-late-fee`, { method: 'POST', body });
  },

  sendCustomerStatement(customerId: string, body: { asOf?: string } = {}) {
    return request<{
      sent: boolean;
      invoiceCount: number;
      totalBalanceCents: number;
      asOf: string;
    }>(`/v1/customers/${customerId}/statement/send`, { method: 'POST', body });
  },

  /**
   * Fetch the customer statement PDF as a Blob (sends the access token
   * as a Bearer header) and return a transient object URL the caller
   * can `window.open()` for an in-tab preview. The earlier
   * `customerStatementUrl()` returned the raw API URL, but opening
   * that in a new tab triggers an unauthenticated GET (browsers don't
   * carry custom headers across `window.open`) and 401s. The blob URL
   * is local-only so no auth header is needed for the open call.
   *
   * Caller should `URL.revokeObjectURL(returnedUrl)` once the tab is
   * closed if doing many of these in a session; for the one-off
   * Preview button it's fine to leak (browser GCs on tab close).
   */
  async customerStatementPdfBlobUrl(
    customerId: string,
    params: { asOf?: string } = {},
  ): Promise<string> {
    const qs = new URLSearchParams();
    if (params.asOf) qs.set('asOf', params.asOf);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    const token = await getAuthToken();
    const headers: Record<string, string> = { Accept: 'application/pdf' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const cid = getClientId();
    if (cid) headers['X-Client-Id'] = cid;
    const res = await fetch(`${BASE}/v1/customers/${customerId}/statement.pdf${suffix}`, {
      method: 'GET',
      headers,
    });
    if (!res.ok) {
      const contentType = res.headers.get('content-type') ?? '';
      const body = contentType.includes('json') ? await res.json() : await res.text();
      throw new ApiError(res.status, body);
    }
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  },

  updateInvoice(
    id: string,
    body: {
      txnDate?: string;
      dueDate?: string;
      customerMemo?: string | null;
      privateNote?: string | null;
      lines?: Array<{
        itemId?: string;
        description: string;
        qty: number;
        unitPriceCents: number;
        incomeAccountId?: string;
        taxCodeId?: string;
      }>;
    },
  ) {
    return request<{ invoice: Invoice }>(`/v1/invoices/${id}`, { method: 'PATCH', body });
  },

  voidInvoice(id: string, body: { reason: string; voidDate?: string }) {
    return request<{ reversalEntryId: string }>(`/v1/invoices/${id}/void`, {
      method: 'POST',
      body,
    });
  },
  getArAging(params: { asOf?: string } = {}) {
    const qs = new URLSearchParams();
    if (params.asOf) qs.set('asOf', params.asOf);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request<{
      asOf: string;
      grandTotalCents: number;
      grandTotals: Record<string, number>;
      customers: Array<{
        customerId: string;
        customerName: string;
        balanceCents: number;
        buckets: Record<string, number>;
        invoices: Array<{
          invoiceId: string;
          docNumber: string;
          dueDate: string;
          daysOverdue: number;
          balanceCents: number;
          bucket: string;
        }>;
      }>;
    }>(`/v1/reports/ar-aging${suffix}`);
  },

  createCreditMemo(body: {
    customerId: string;
    invoiceId?: string;
    totalCents: number;
    reason?: string;
    txnDate?: string;
  }) {
    return request<{
      creditMemo: {
        id: string;
        customerId: string;
        invoiceId: string | null;
        docNumber: string;
        txnDate: string;
        reason: string | null;
        totalCents: number;
        createdAt: string;
      };
      journalEntryId: string;
    }>('/v1/credit-memos', { method: 'POST', body });
  },

  listUndepositedPayments() {
    return request<{
      payments: Array<{
        id: string;
        txnDate: string;
        totalCents: number;
        method: string;
        stripePaymentIntentId: string | null;
        customerId: string;
        customerName: string;
        invoiceId: string | null;
        invoiceDocNumber: string | null;
      }>;
    }>('/v1/deposits/undeposited');
  },

  createDeposit(body: {
    bankAccountId: string;
    paymentIds: string[];
    txnDate?: string;
    notes?: string;
  }) {
    return request<{
      deposit: {
        id: string;
        bankAccountId: string;
        txnDate: string;
        totalCents: number;
        notes: string | null;
        journalEntryId: string | null;
        lineCount: number;
        createdAt: string;
      };
    }>('/v1/deposits', { method: 'POST', body });
  },

  listDeposits() {
    return request<{
      deposits: Array<{
        id: string;
        bankAccountId: string;
        txnDate: string;
        totalCents: number;
        notes: string | null;
        journalEntryId: string | null;
        lineCount: number;
        createdAt: string;
      }>;
    }>('/v1/deposits');
  },

  listCreditMemos(params: { customerId?: string; invoiceId?: string } = {}) {
    const qs = new URLSearchParams();
    if (params.customerId) qs.set('customerId', params.customerId);
    if (params.invoiceId) qs.set('invoiceId', params.invoiceId);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request<{
      creditMemos: Array<{
        id: string;
        customerId: string;
        invoiceId: string | null;
        docNumber: string;
        txnDate: string;
        reason: string | null;
        totalCents: number;
        createdAt: string;
      }>;
    }>(`/v1/credit-memos${suffix}`);
  },

  getEngagementLetterTemplate() {
    return request<EngagementLetterTemplate>('/v1/engagement-letter/template');
  },

  acceptEngagementLetter(clientId: string, payload: EngagementLetterAcceptRequest) {
    return request<{ documentId: string; acceptedAt: string; templateVersion: string }>(
      '/v1/engagement-letter/accept',
      { method: 'POST', body: payload, clientId },
    );
  },

  // --- Bookkeeping (Phase 2) ---

  getDashboardSummary() {
    return request<DashboardSummary>('/v1/dashboard/summary');
  },

  getAccounts() {
    return request<AccountList>('/v1/accounts');
  },

  getLedgerEntries(
    params: {
      limit?: number;
      cursor?: string;
      source?: string | string[];
      from?: string;
      to?: string;
    } = {},
  ) {
    const qs = new URLSearchParams();
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.cursor) qs.set('cursor', params.cursor);
    if (params.source) {
      qs.set('source', Array.isArray(params.source) ? params.source.join(',') : params.source);
    }
    if (params.from) qs.set('from', params.from);
    if (params.to) qs.set('to', params.to);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request<LedgerEntryList>(`/v1/ledger/entries${suffix}`);
  },

  getIncomeStatement(params: { from?: string; to?: string } = {}) {
    const qs = new URLSearchParams();
    if (params.from) qs.set('from', params.from);
    if (params.to) qs.set('to', params.to);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request<IncomeStatement>(`/v1/reports/income-statement${suffix}`);
  },

  getTrialBalance(params: { asOf?: string; from?: string } = {}) {
    const qs = new URLSearchParams();
    if (params.asOf) qs.set('asOf', params.asOf);
    if (params.from) qs.set('from', params.from);
    const suffix = qs.toString() ? `?${qs}` : '';
    return request<TrialBalance>(`/v1/reports/trial-balance${suffix}`);
  },

  getBalanceSheet(params: { asOf?: string; from?: string } = {}) {
    const qs = new URLSearchParams();
    if (params.asOf) qs.set('asOf', params.asOf);
    if (params.from) qs.set('from', params.from);
    const suffix = qs.toString() ? `?${qs}` : '';
    return request<{
      asOf: string;
      assets: Array<{ accountId: string; code: string; name: string; amountCents: number }>;
      liabilities: Array<{ accountId: string; code: string; name: string; amountCents: number }>;
      equity: Array<{ accountId: string; code: string; name: string; amountCents: number }>;
      totalAssetsCents: number;
      totalLiabilitiesCents: number;
      totalEquityCents: number;
      balanced: boolean;
    }>(`/v1/reports/balance-sheet${suffix}`);
  },

  getReportPeriodBounds() {
    return request<{ earliest: string | null; latest: string | null }>('/v1/reports/period-bounds');
  },

  /**
   * Triggers a browser download of one financial statement in the chosen
   * format. Skips the typed JSON helper because the response is a binary
   * file with a Content-Disposition header — we ask fetch for a Blob,
   * pull the filename out of the header, and pop an anchor click.
   */
  /**
   * Free-form reviewer notes attached to a specific report instance.
   * The body is whatever the reviewer types — saved per
   * (engagement, reportKind, from, to). Empty body deletes the row.
   */
  getReportNote(params: {
    reportKind: 'income_statement' | 'balance_sheet' | 'cash_flow' | 'trial_balance';
    from: string;
    to: string;
  }) {
    const qs = new URLSearchParams({
      reportKind: params.reportKind,
      from: params.from,
      to: params.to,
    });
    return request<{
      note: { id: string; body: string; updatedAt: string; updatedBy: string | null } | null;
    }>(`/v1/reports/notes?${qs.toString()}`);
  },

  upsertReportNote(params: {
    reportKind: 'income_statement' | 'balance_sheet' | 'cash_flow' | 'trial_balance';
    from: string;
    to: string;
    body: string;
  }) {
    return request<{
      note: { id: string; body: string; updatedAt: string; updatedBy: string | null } | null;
    }>('/v1/reports/notes', { method: 'PUT', body: params });
  },

  /**
   * Columnar P&L — one row per account, one column per month, plus a
   * Total column. The "All" tab on Reports renders this when the period
   * spans more than one calendar month.
   */
  getIncomeStatementByMonth(params: { from?: string; to?: string } = {}) {
    const qs = new URLSearchParams();
    if (params.from) qs.set('from', params.from);
    if (params.to) qs.set('to', params.to);
    const suffix = qs.toString() ? `?${qs}` : '';
    return request<{
      from: string;
      to: string;
      months: string[];
      rows: Array<{
        accountId: string;
        code: string;
        name: string;
        section: 'revenue' | 'cost_of_revenue' | 'expense';
        monthlyCents: number[];
        totalCents: number;
      }>;
      totalsRevenue: { monthlyCents: number[]; totalCents: number };
      totalsCostOfRevenue: { monthlyCents: number[]; totalCents: number };
      grossProfit: { monthlyCents: number[]; totalCents: number };
      totalsExpenses: { monthlyCents: number[]; totalCents: number };
      netIncome: { monthlyCents: number[]; totalCents: number };
    }>(`/v1/reports/income-statement/by-month${suffix}`);
  },

  /**
   * Per-month headline figures for every report. Powers the "All" tab's
   * row-per-month summary table.
   */
  getMonthlySummary(params: { from?: string; to?: string } = {}) {
    const qs = new URLSearchParams();
    if (params.from) qs.set('from', params.from);
    if (params.to) qs.set('to', params.to);
    const suffix = qs.toString() ? `?${qs}` : '';
    return request<{
      from: string;
      to: string;
      months: Array<{
        month: string;
        monthStart: string;
        monthEnd: string;
        revenueCents: number;
        expensesCents: number;
        netIncomeCents: number;
        totalAssetsCents: number;
        totalLiabilitiesCents: number;
        totalEquityCents: number;
        operatingCfCents: number;
        investingCfCents: number;
        financingCfCents: number;
        netChangeInCashCents: number;
        totalDebitCents: number;
        totalCreditCents: number;
        balanced: boolean;
      }>;
    }>(`/v1/reports/monthly-summary${suffix}`);
  },

  getCashFlow(params: { from?: string; to?: string } = {}) {
    const qs = new URLSearchParams();
    if (params.from) qs.set('from', params.from);
    if (params.to) qs.set('to', params.to);
    const suffix = qs.toString() ? `?${qs}` : '';
    return request<{
      from: string;
      to: string;
      netIncomeCents: number;
      operating: {
        items: Array<{ accountId: string | null; code: string; name: string; amountCents: number }>;
        totalCents: number;
      };
      investing: {
        items: Array<{ accountId: string | null; code: string; name: string; amountCents: number }>;
        totalCents: number;
      };
      financing: {
        items: Array<{ accountId: string | null; code: string; name: string; amountCents: number }>;
        totalCents: number;
      };
      netChangeInCashCents: number;
      beginningCashCents: number;
      endingCashCents: number;
    }>(`/v1/reports/cash-flow${suffix}`);
  },

  async downloadReport(
    name:
      | 'income-statement'
      | 'income-statement-by-month'
      | 'trial-balance'
      | 'balance-sheet'
      | 'cash-flow'
      | 'gl-summary'
      | 'all',
    format: 'pdf' | 'xlsx' | 'csv',
    params: { from?: string; to?: string; asOf?: string; letterhead?: boolean },
  ): Promise<void> {
    const qs = new URLSearchParams({ format });
    if (params.from) qs.set('from', params.from);
    if (params.to) qs.set('to', params.to);
    if (params.asOf) qs.set('asOf', params.asOf);
    if (params.letterhead) qs.set('letterhead', '1');
    const token = await getAuthToken();
    const exportHeaders: Record<string, string> = {};
    if (token) exportHeaders['Authorization'] = `Bearer ${token}`;
    const exportCid = getClientId();
    if (exportCid) exportHeaders['X-Client-Id'] = exportCid;
    const res = await fetch(`${BASE}/v1/reports/${name}/export?${qs.toString()}`, {
      headers: exportHeaders,
    });
    if (!res.ok) {
      let detail = `HTTP ${res.status}`;
      try {
        const body = await res.json();
        detail =
          (body as { detail?: string; title?: string }).detail ??
          (body as { title?: string }).title ??
          detail;
      } catch {
        /* not JSON */
      }
      throw new ApiError(res.status, detail);
    }
    const blob = await res.blob();
    // RFC 6266 filename parsing — server uses `filename="…"` so a regex is
    // enough; if it's missing we fall back to a sensible default.
    const disposition = res.headers.get('content-disposition') ?? '';
    const match = /filename="?([^"]+)"?/.exec(disposition);
    const filename = match?.[1] ?? `${name}.${format}`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },

  /**
   * Reclassify a pending AI proposal: swap one or both account codes.
   * Used on the Documents page before approval.
   */
  editProposal(id: string, body: { debitAccountCode?: string; creditAccountCode?: string }) {
    return request<{ proposalId: string; status: 'pending' }>(`/v1/proposals/${id}`, {
      method: 'PATCH',
      body,
    });
  },

  /**
   * Post-hoc reclassification on an already-posted journal entry — swaps
   * one line's account. Used on the Activity page so Brickstone can fix
   * "7900 Uncategorized Expense" entries (or any other AI misjudgment)
   * after they land. The amount stays the same so the entry stays
   * balanced.
   */
  reclassifyLine(entryId: string, lineId: string, accountId: string) {
    return request<{
      entryId: string;
      lineId: string;
      account: { id: string; code: string; name: string };
    }>(`/v1/ledger/entries/${entryId}/lines/${lineId}`, {
      method: 'PATCH',
      body: { accountId },
    });
  },

  /**
   * Every pending proposal for the tenant, with the per-row data the
   * Activity inline review section needs. Works for orphan proposals
   * (no source document) too.
   */
  getPendingProposals() {
    return request<{
      proposals: Array<{
        id: string;
        confidence: number;
        createdAt: string;
        sourceDocumentId: string | null;
        sourceDocumentFilename: string | null;
        transaction: {
          date: string;
          description: string;
          amount_cents: number;
          direction?: 'debit' | 'credit';
        };
        proposedEntry: {
          description: string;
          posted_on: string;
          lines: Array<{
            account_code: string;
            account_name: string;
            side: 'debit' | 'credit';
            amount_cents: number;
          }>;
        };
      }>;
    }>('/v1/proposals/pending');
  },

  bulkApproveProposals(ids: string[]) {
    return request<{
      approved: number;
      skipped: number;
      failed: number;
      outcomes: Array<{
        id: string;
        status: 'approved' | 'skipped' | 'failed';
        reason?: string;
        entryId?: string;
      }>;
    }>('/v1/proposals/bulk-approve', { method: 'POST', body: { ids } });
  },

  bulkRejectProposals(ids: string[], reason?: string) {
    return request<{
      rejected: number;
      skipped: number;
      outcomes: Array<{ id: string; status: 'rejected' | 'skipped'; reason?: string }>;
    }>('/v1/proposals/bulk-reject', { method: 'POST', body: reason ? { ids, reason } : { ids } });
  },

  approveProposal(id: string) {
    return request<{ proposalId: string; entryId: string; status: 'approved' }>(
      `/v1/proposals/${id}/approve`,
      { method: 'POST', body: {} },
    );
  },

  rejectProposal(id: string, reason?: string) {
    return request<{ proposalId: string; status: 'rejected' }>(`/v1/proposals/${id}/reject`, {
      method: 'POST',
      body: reason ? { reason } : {},
    });
  },

  reverseToReview(journalEntryId: string) {
    return request<{ proposalId: string; status: 'pending' }>(
      `/v1/journal-entries/${journalEntryId}/reverse-to-review`,
      { method: 'POST', body: {} },
    );
  },

  bulkReverseToReview(ids: string[]) {
    return request<{
      reversed: number;
      skipped: number;
      failed: number;
      outcomes: Array<{
        id: string;
        status: 'reversed' | 'skipped' | 'failed';
        reason?: string;
        proposalId?: string;
      }>;
    }>('/v1/journal-entries/bulk-reverse-to-review', { method: 'POST', body: { ids } });
  },

  getPendingReviewSummary() {
    return request<{
      totalPending: number;
      byDocument: Array<{ documentId: string | null; filename: string | null; count: number }>;
    }>('/v1/ledger/pending-review-summary');
  },

  getGlSummary(params: { from?: string; to?: string } = {}) {
    const qs = new URLSearchParams();
    if (params.from) qs.set('from', params.from);
    if (params.to) qs.set('to', params.to);
    const suffix = qs.toString() ? `?${qs}` : '';
    return request<{
      from: string;
      to: string;
      accounts: Array<{
        accountId: string;
        code: string;
        name: string;
        kind: 'asset' | 'liability' | 'equity' | 'revenue' | 'cost_of_revenue' | 'expense';
        normalSide: 'debit' | 'credit';
        openingCents: number;
        periodDebitCents: number;
        periodCreditCents: number;
        closingCents: number;
      }>;
    }>(`/v1/reports/gl-summary${suffix}`);
  },

  createJournalEntry(payload: JournalEntryCreate) {
    return request<{ id: string }>('/v1/ledger/entries', { method: 'POST', body: payload });
  },

  /** Dev/staging only — populates representative books. 404s in production. */
  seedLedger() {
    return request<{ seeded: number; alreadyPresent: boolean }>('/v1/dev/seed-ledger', {
      method: 'POST',
    });
  },

  // --- Documents ---

  getDocuments() {
    return request<DocumentList>('/v1/documents');
  },

  requestUploadUrl(body: DocumentUploadUrlRequest) {
    return request<DocumentUploadUrlResponse>('/v1/documents/upload-url', { method: 'POST', body });
  },

  confirmDocument(body: DocumentCreateRequest) {
    return request<Document>('/v1/documents', { method: 'POST', body });
  },

  getDocumentDownloadUrl(id: string) {
    return request<DocumentDownloadUrlResponse>(`/v1/documents/${id}/download-url`);
  },

  // --- Periods + closing ---

  listPeriods() {
    return request<{
      periods: Array<{
        id: string;
        periodStart: string;
        periodEnd: string;
        status: 'open' | 'in_review' | 'closed' | 'locked';
        closedAt: string | null;
        closedByUserId: string | null;
        reopenedAt: string | null;
        reopenReason: string | null;
        entryCount: number;
      }>;
    }>('/v1/periods');
  },

  closePeriod(periodId: string) {
    return request<{
      periodId: string;
      status: 'closed';
      closedAt: string;
      netIncomeCents: number;
      entryCount: number;
      snapshots: {
        incomeStatement: IncomeStatement;
        trialBalance: TrialBalance;
      };
    }>(`/v1/periods/${periodId}/close`, { method: 'POST', body: {} });
  },

  reopenPeriod(periodId: string, reason: string) {
    return request<{ periodId: string; status: 'open'; reopenedAt: string; reason: string }>(
      `/v1/periods/${periodId}/reopen`,
      { method: 'POST', body: { reason } },
    );
  },

  // --- GL detail ---

  getGlDetail(
    params:
      | { accountId: string; from: string; to: string }
      | { accountIds: string[]; from: string; to: string },
  ) {
    const qs = new URLSearchParams();
    if ('accountIds' in params) {
      qs.set('accountIds', params.accountIds.join(','));
    } else {
      qs.set('accountId', params.accountId);
    }
    qs.set('from', params.from);
    qs.set('to', params.to);
    return request<{
      // Present only when called with single accountId (back-compat).
      account?: {
        id: string;
        code: string;
        name: string;
        kind: string;
        normalSide: 'debit' | 'credit';
      };
      // Always present.
      accounts: Array<{
        id: string;
        code: string;
        name: string;
        kind: string;
        normalSide: 'debit' | 'credit';
      }>;
      from: string;
      to: string;
      openingBalanceCents: number;
      closingBalanceCents: number;
      lines: Array<{
        entryId: string;
        postedAt: string;
        description: string;
        memo: string | null;
        accountId: string;
        accountCode: string;
        accountName: string;
        debitCents: number;
        creditCents: number;
        /** null on multi-account drills — running balance is meaningless. */
        balanceCents: number | null;
      }>;
    }>(`/v1/reports/gl-detail?${qs}`);
  },

  // --- CoA mutations ---

  createAccount(payload: {
    code: string;
    name: string;
    kind: 'asset' | 'liability' | 'equity' | 'revenue' | 'cost_of_revenue' | 'expense';
    normalSide?: 'debit' | 'credit';
    parentId?: string | null;
    taxLineHint?: string;
  }) {
    return request<unknown>('/v1/accounts', { method: 'POST', body: payload });
  },

  patchAccount(
    id: string,
    payload: { name?: string; isArchived?: boolean; parentId?: string | null },
  ) {
    return request<unknown>(`/v1/accounts/${id}`, { method: 'PATCH', body: payload });
  },

  getDocumentExtraction(id: string) {
    return request<{
      model: string;
      confidence: number | null;
      citedPages: number[];
      createdAt: string;
      payload: {
        transactions: Array<{
          date: string;
          description: string;
          amount_cents: number;
          direction?: 'debit' | 'credit';
          cited_page?: number;
        }>;
        notes?: string;
      };
    }>(`/v1/documents/${id}/extraction`);
  },

  openBillingPortal() {
    return request<{ url: string }>('/v1/billing/portal', { method: 'POST' });
  },

  getSubscription() {
    return request<{
      subscription: {
        status: string;
        tier: string;
        cancelAtPeriodEnd: boolean;
        currentPeriodEnd: string | null;
      } | null;
    }>('/v1/billing/subscription');
  },
  cancelSubscription() {
    return request<{ ok: true; cancelAtPeriodEnd: boolean; currentPeriodEnd: string | null }>(
      '/v1/billing/cancel',
      { method: 'POST' },
    );
  },
  resumeSubscription() {
    return request<{ ok: true; cancelAtPeriodEnd: boolean }>('/v1/billing/resume', {
      method: 'POST',
    });
  },

  // --- Bank connections (Stripe Financial Connections) ---
  startBankConnectionSession() {
    return request<{ clientSecret: string; sessionId: string; publishableKey: string | null }>(
      '/v1/bank-connections/session',
      { method: 'POST' },
    );
  },
  persistBankConnections(sessionId: string) {
    return request<{ connections: BankConnection[] }>('/v1/bank-connections', {
      method: 'POST',
      body: { sessionId },
    });
  },
  listBankConnections() {
    return request<{ connections: BankConnection[] }>('/v1/bank-connections');
  },
  syncBankConnection(id: string) {
    return request<{ imported: number; total: number }>(`/v1/bank-connections/${id}/sync`, {
      method: 'POST',
    });
  },
  disconnectBankConnection(id: string) {
    return request<void>(`/v1/bank-connections/${id}`, { method: 'DELETE' });
  },

  deleteDocument(id: string) {
    return request<void>(`/v1/documents/${id}`, { method: 'DELETE' });
  },

  getDocumentProposals(id: string) {
    return request<{
      proposals: Array<{
        id: string;
        transaction: {
          date: string;
          description: string;
          amount_cents: number;
          direction?: 'debit' | 'credit';
          cited_page?: number;
        };
        proposedEntry: {
          description: string;
          posted_on: string;
          lines: Array<{
            account_code: string;
            account_name: string;
            side: 'debit' | 'credit';
            amount_cents: number;
          }>;
        };
        rationale: string;
        confidence: number;
        model: string;
        status: 'pending' | 'approved' | 'rejected' | 'stale';
        createdAt: string;
      }>;
    }>(`/v1/documents/${id}/proposals`);
  },
};

/**
 * PUT a file directly to a GCS signed URL. Deliberately a bare fetch — no auth
 * header (the signed URL carries its own credentials) and the Content-Type must
 * match the type the URL was signed for.
 */
export async function putToSignedUrl(url: string, file: File, mime: string): Promise<void> {
  const res = await fetch(url, { method: 'PUT', headers: { 'Content-Type': mime }, body: file });
  if (!res.ok) {
    throw new Error(`Upload failed (${res.status})`);
  }
}

// ---------------------------------------------------------------------------
// Admin (cross-tenant) — only renders when the JWT carries
// userKind=staff + a partner-tier or platform_admin staff_role. Customers
// hitting the routes get 404, so the menu items themselves are conditionally
// rendered based on the user's role from /v1/me.
// ---------------------------------------------------------------------------

export type AdminClientListItem = {
  id: string;
  legalName: string;
  displayName: string;
  kind: 'business' | 'individual';
  planTier: 'starter' | 'professional_plus' | 'business_plus';
  status: string;
  ownerEmail: string | null;
  createdAt: string;
  archivedAt: string | null;
};

export type AdminClientDetail = AdminClientListItem & {
  country: string;
  stateCode: string | null;
  updatedAt: string;
  owner: { email: string; fullName: string | null; createdAt: string } | null;
  engagement: {
    id: string;
    fiscalYearStart: string;
    basis: 'cash' | 'accrual';
    active: boolean;
    createdAt: string;
  } | null;
  subscription: {
    id: string;
    stripeSubscriptionId: string | null;
    stripeCustomerId: string | null;
    tier: string;
    status: string;
    currentPeriodStart: string | null;
    currentPeriodEnd: string | null;
    cancelAtPeriodEnd: boolean;
    trialEnd: string | null;
  } | null;
  counts: {
    accounts: number;
    documents: number;
    proposalsPending: number;
    journalEntries: number;
  };
};

export type AdminAuditEvent = {
  id: string;
  clientId: string | null;
  clientDisplayName: string | null;
  actorUserId: string | null;
  actorEmail: string | null;
  actorKind: string;
  action: string;
  targetTable: string | null;
  targetId: string | null;
  status: string;
  ip: string | null;
  userAgent: string | null;
  requestId: string | null;
  createdAt: string;
};

export type AdminFeatureFlag = {
  key: string;
  value: unknown;
  description: string | null;
  updatedBy?: string | null;
  updatedAt: string;
};

export type AdminStaffItem = {
  id: string;
  email: string;
  fullName: string | null;
  staffRole: 'platform_admin' | 'partner' | 'manager' | 'accountant' | 'reviewer';
  status: string;
  mfaEnrolledAt: string | null;
  lastLoginAt: string | null;
  createdAt: string;
};

export type SeatConfig = {
  tiers: Array<{ tier: string; includedSeats: number }>;
  extraSeatFeeCents: number | null;
  stripeSeatPriceId: string | null;
  stripeSeatPriceIdAnnual: string | null;
};

export type AiConfig = {
  tiers: Array<{ tier: string; monthlyCapUsd: number | null }>;
};

export const adminApi = {
  // --- Collaborator seat settings (platform_admin only) ---
  getSeatConfig() {
    return request<SeatConfig>('/v1/admin/platform/seat-config');
  },
  updateSeatConfig(body: {
    tiers?: Array<{ tier: string; includedSeats: number }>;
    extraSeatFeeCents?: number | null;
    stripeSeatPriceId?: string | null;
    stripeSeatPriceIdAnnual?: string | null;
  }) {
    return request<{ ok: boolean }>('/v1/admin/platform/seat-config', { method: 'PUT', body });
  },

  // --- AI monthly cost caps (platform_admin only) ---
  getAiConfig() {
    return request<AiConfig>('/v1/admin/platform/ai-config');
  },
  updateAiConfig(body: { tiers: Array<{ tier: string; monthlyCapUsd: number | null }> }) {
    return request<{ ok: boolean }>('/v1/admin/platform/ai-config', { method: 'PUT', body });
  },

  // --- Referral program (platform_admin only) ---
  listReferralCommissions(status: 'accrued' | 'approved' | 'held' | 'paid' = 'accrued') {
    return request<{ commissions: AdminReferralCommission[] }>(
      `/v1/admin/platform/referral-commissions?status=${status}`,
    );
  },
  decideReferralCommissions(ids: string[], decision: 'approve' | 'hold' | 'release') {
    return request<{ decision: string; updated: number }>(
      '/v1/admin/platform/referral-commissions/decision',
      { method: 'POST', body: { ids, decision } },
    );
  },
  runReferralPayouts() {
    return request<{ partnersConsidered: number; paidCount: number; totalPaidCents: number }>(
      '/v1/admin/platform/referral-payouts/run',
      { method: 'POST', body: {} },
    );
  },

  // Partner-tier visibility — both partner and platform_admin reach these.
  listClients(
    params: {
      q?: string;
      status?: string;
      plan?: string;
      includeArchived?: boolean;
      limit?: number;
      offset?: number;
    } = {},
  ) {
    const qs = new URLSearchParams();
    if (params.q) qs.set('q', params.q);
    if (params.status) qs.set('status', params.status);
    if (params.plan) qs.set('plan', params.plan);
    if (params.includeArchived) qs.set('includeArchived', 'true');
    if (params.limit != null) qs.set('limit', String(params.limit));
    if (params.offset != null) qs.set('offset', String(params.offset));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request<{ total: number; clients: AdminClientListItem[] }>(`/v1/admin/clients${suffix}`);
  },

  getClient(id: string) {
    return request<AdminClientDetail>(`/v1/admin/clients/${id}`);
  },

  archiveClient(id: string) {
    return request<{ id: string; archivedAt: string }>(`/v1/admin/clients/${id}/archive`, {
      method: 'POST',
    });
  },

  restoreClient(id: string) {
    return request<{ id: string; archivedAt: null }>(`/v1/admin/clients/${id}/restore`, {
      method: 'POST',
    });
  },

  setClientPlanTier(id: string, planTier: 'starter' | 'professional_plus' | 'business_plus') {
    return request<{ id: string; planTier: string }>(`/v1/admin/clients/${id}/plan-tier`, {
      method: 'POST',
      body: { planTier },
    });
  },

  cancelClientSubscription(id: string) {
    return request<{
      ok: boolean;
      clientId: string;
      subscriptionId: string;
      previousStatus: string;
      stripeOutcome: 'ok' | 'skipped' | 'failed';
      stripeError?: string;
    }>(`/v1/admin/clients/${id}/cancel-subscription`, { method: 'POST' });
  },

  listAudit(
    params: {
      actor?: string;
      action?: string;
      clientId?: string;
      from?: string;
      to?: string;
      status?: 'success' | 'failed';
      limit?: number;
      offset?: number;
    } = {},
  ) {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v != null && v !== '') qs.set(k, String(v));
    }
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request<{ total: number; events: AdminAuditEvent[] }>(`/v1/admin/audit${suffix}`);
  },

  // Platform-admin-only — IT-side surface (role management).
  listStaff() {
    return request<{ staff: AdminStaffItem[] }>('/v1/admin/platform/staff');
  },

  promoteStaff(payload: { email: string; staffRole: AdminStaffItem['staffRole'] }) {
    return request<{ user: { id: string; email: string; staff_role: string; status: string } }>(
      '/v1/admin/platform/staff',
      { method: 'POST', body: payload },
    );
  },

  demoteStaff(userId: string) {
    return request<{ ok: boolean; email: string; noop: boolean }>(
      `/v1/admin/platform/staff/${userId}`,
      { method: 'DELETE' },
    );
  },

  getDemoStats(olderThanDays = 7) {
    return request<{ total: number; eligible: number; cutoffDays: number }>(
      `/v1/admin/platform/demo-stats?olderThanDays=${olderThanDays}`,
    );
  },

  sweepDemoUsers(olderThanDays = 7) {
    return request<{ usersDeleted: number; clientsDeleted: number; cutoffDays: number }>(
      '/v1/admin/platform/demo-sweep',
      { method: 'POST', body: { olderThanDays } },
    );
  },

  listFlags() {
    return request<{ flags: AdminFeatureFlag[] }>('/v1/admin/platform/flags');
  },

  upsertFlag(key: string, value: unknown, description?: string | null) {
    return request<{ flag: AdminFeatureFlag }>(
      `/v1/admin/platform/flags/${encodeURIComponent(key)}`,
      {
        method: 'PUT',
        body: { value, description },
      },
    );
  },

  deleteFlag(key: string) {
    return request<{ ok: boolean; key: string; deleted: boolean }>(
      `/v1/admin/platform/flags/${encodeURIComponent(key)}`,
      { method: 'DELETE' },
    );
  },
};
